import { dcFetchJson, lawvoFetchJson } from './httpClient';
export interface SelectOption {
  value: string;
  label: string;
}
function unwrapList(payload: unknown): Record<string, unknown>[] {
  if (payload == null) return [];
  if (Array.isArray(payload)) return payload;
  if (typeof payload !== 'object') return [];
  const record = payload as Record<string, unknown>;
  const candidates = [record.data, record.results, record.items, record.rows];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate as Record<string, unknown>[];
    if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
      const nested = candidate as Record<string, unknown>;
      if (Array.isArray(nested.data)) return nested.data as Record<string, unknown>[];
    }
  }
  return [];
}
function normalizeItem(item: Record<string, unknown>): Record<string, unknown> {
  if (item.attributes && typeof item.attributes === 'object' && !Array.isArray(item.attributes)) {
    return {
      id: item.id,
      ...(item.attributes as Record<string, unknown>)
    };
  }
  return item;
}
function toOptions(payload: unknown, valueKey = 'id', labelKeys: string[] = ['name', 'title', 'tagName', 'email']): SelectOption[] {
  const items = unwrapList(payload).map(normalizeItem);
  return items.map(item => {
    const rawValue = item[valueKey];
    if (rawValue === undefined || rawValue === null) return null;
    const label = labelKeys.map(key => item[key]).find(v => typeof v === 'string' && v.trim()) || String(rawValue);
    const extra = item.email && item.firstName ? ` (${[item.firstName, item.lastName].filter(Boolean).join(' ')})` : '';
    return {
      value: String(rawValue),
      label: `${label}${extra}`
    };
  }).filter((o): o is SelectOption => o !== null);
}
async function fetchLawvoOptions(path: string, valueKey = 'id', labelKeys?: string[]): Promise<SelectOption[]> {
  const payload = await lawvoFetchJson<unknown>(path);
  return toOptions(payload, valueKey, labelKeys);
}
async function fetchDcOptions(path: string, valueKey = 'id', labelKeys?: string[]): Promise<SelectOption[]> {
  const payload = await dcFetchJson<unknown>(path);
  return toOptions(payload, valueKey, labelKeys);
}
export async function fetchProvinces(): Promise<SelectOption[]> {
  return fetchLawvoOptions('/provinces?$limit=100&$sort[name]=1');
}
export async function fetchServiceCategories(): Promise<SelectOption[]> {
  return fetchLawvoOptions('/service-categories?deleted=false&publish=true&$limit=200&$sort[name]=1');
}
export async function fetchLawyers(): Promise<SelectOption[]> {
  const pageSize = 100;
  let skip = 0;
  const all: SelectOption[] = [];
  while (true) {
    const batch = await fetchLawvoOptions(`/users?role=lawyer&$limit=${pageSize}&$skip=${skip}&$sort[email]=1`, 'id', ['email', 'firstName', 'lastName']);
    all.push(...batch);
    if (batch.length < pageSize) break;
    skip += pageSize;
  }
  return all;
}
export async function fetchDocumentTemplateTags(): Promise<SelectOption[]> {
  try {
    return await fetchDcOptions('/dc-tags?_limit=500', 'id', ['tagName', 'name', 'title']);
  } catch {
    return [];
  }
}
export async function fetchDocumentCenterServices(): Promise<SelectOption[]> {
  try {
    return await fetchDcOptions('/dc-service-categories?active=true&deleted=false&_limit=200');
  } catch {
    return [];
  }
}
export const DOCUMENT_CAPABILITIES = [{
  key: 'create',
  label: 'Create Documents',
  permission: null
}, {
  key: 'download',
  label: 'Download Documents',
  permission: 'downloadDocuments'
}, {
  key: 'share',
  label: 'Share Documents',
  permission: 'shareDocuments'
}, {
  key: 'review',
  label: 'Request Legal Document Review',
  permission: 'legalDocumentReview'
}, {
  key: 'esign',
  label: 'E-Signature',
  permission: 'eSignature'
}, {
  key: 'library',
  label: 'Access Legal Document Library',
  permission: 'legalDocumentLibrary'
}] as const;
export type DocumentCapabilityKey = (typeof DOCUMENT_CAPABILITIES)[number]['key'];